/**
 * Rezervasyon Sayfası — liste + takvim görünüm + detay drawer.
 *
 * Tenant-aware: useReservations zaten JWT'den tenant'ı çıkarır.
 */
import {
  ActionIcon, Badge, Button, Card, Drawer, Group, Loader, ScrollArea,
  Stack, Tabs, Text, Modal, Select, NumberInput, TextInput, Textarea,
} from "@mantine/core";
import { DateTimePicker, DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconList, IconPlus } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteResource, useResource, useAction, useUpdateReservation, apiClient } from "../../../shared/api";
import {
  EmptyState, InfiniteTable, MoneyDisplay, PageHeader, StatusBadge, formatDateTime,
  validatePhoneTR, apiErrorMessage, apiFieldErrors,
} from "../../../shared/ui";

export function ReservationsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<"list" | "calendar">("list");
  const [calendarDay, setCalendarDay] = useState<Date>(new Date());
  const [createOpen, createHandlers] = useDisclosure(false);
  const [editOpen, editHandlers] = useDisclosure(false);
  const [depositOpen, depositHandlers] = useDisclosure(false);
  const [cancelOpen, cancelHandlers] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState("");
  // Sunucu tarafı sıralama — sütun başlığına tıklayınca tüm sayfalar (infinite
  // scroll) doğru sıralanır. DRF `ordering` formatı: "starts_at" / "-starts_at".
  // Varsayılan backend ile aynı: en yeni/yakın rezervasyon üstte.
  const [sort, setSort] = useState<string | null>("-starts_at");
  // reservations viewset'inde search_fields yok — arama eklenmiyor.
  const {
    items: reservations,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("reservations", sort ? { filters: { ordering: sort } } : {});

  const selected = reservations.find((r: any) => r.id === selectedId);

  const seat = useAction("reservations", "seat");
  const cancel = useAction("reservations", "cancel-reservation");

  async function doSeat() {
    if (!selected) return;
    try {
      await seat.mutateAsync({ id: selected.id });
      notifications.show({ message: "Misafirler masaya geçirildi", color: "green" });
      setSelectedId(null);
    } catch (e: any) {
      notifications.show({ message: apiErrorMessage(e), color: "red" });
    }
  }

  function openCancel() {
    setCancelReason("");
    cancelHandlers.open();
  }

  async function confirmCancel() {
    if (!selected) return;
    try {
      await cancel.mutateAsync({ id: selected.id, reason: cancelReason });
      notifications.show({ message: "Rezervasyon iptal edildi", color: "orange" });
      cancelHandlers.close();
      setSelectedId(null);
    } catch (e: any) {
      notifications.show({ message: apiErrorMessage(e), color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Rezervasyonlar"
        subtitle="Masa rezervasyon yönetimi"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={createHandlers.open}>Yeni Rezervasyon</Button>
        }
      />

      <ReservationFormModal opened={createOpen} onClose={createHandlers.close} />
      {selected && (
        <ReservationFormModal
          opened={editOpen}
          onClose={editHandlers.close}
          reservation={selected}
        />
      )}

      <Tabs value={view} onChange={(v) => setView(v as any)}>
        <Tabs.List>
          <Tabs.Tab value="list" leftSection={<IconList size={16} />}>Liste</Tabs.Tab>
          <Tabs.Tab value="calendar" leftSection={<IconCalendar size={16} />}>Takvim</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="list" pt="md">
          <ScrollArea>
            <InfiniteTable
              rows={reservations}
              rowKey={(r) => r.id}
              isLoading={isLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              onRowClick={(r) => setSelectedId(r.id)}
              striped
              sort={sort}
              onSortChange={setSort}
              emptyContent={
                <EmptyState
                  icon="📅"
                  title="Henüz rezervasyon yok"
                  description="Yeni rezervasyon oluşturmak için yukarıdaki butonu kullanın"
                />
              }
              columns={[
                { header: "Tarih/Saat", sortKey: "starts_at", render: (r) => formatDateTime(r.starts_at) },
                { header: "Masa", sortKey: "table__name", render: (r) => `🪑 ${r.table_name || r.table}` },
                { header: "Müşteri", sortKey: "customer_name", render: (r) => r.customer_name || r.customer_phone || "Anonim" },
                { header: "Kişi", sortKey: "party_size", render: (r) => r.party_size },
                {
                  header: "Kapora",
                  render: (r) => (
                    <Stack gap={2}>
                      <Text size="xs"><MoneyDisplay minor={r.deposit_minor} /></Text>
                      <Text size="xs" c="dimmed">
                        ödendi: <MoneyDisplay minor={r.deposit_paid_minor} />
                      </Text>
                      <DepositOutcome outcome={r.deposit_outcome} />
                    </Stack>
                  ),
                },
                { header: "Durum", render: (r) => <StatusBadge status={r.status} /> },
                { header: "Aksiyon", render: () => <ActionIcon variant="subtle">→</ActionIcon> },
              ]}
            />
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="calendar" pt="md">
          {isLoading ? <Loader /> : (
            <CalendarView
              reservations={reservations}
              day={calendarDay}
              onDayChange={setCalendarDay}
              onSelect={setSelectedId}
            />
          )}
        </Tabs.Panel>
      </Tabs>

      <Drawer
        opened={!!selectedId}
        onClose={() => setSelectedId(null)}
        title="Rezervasyon Detay"
        position="right"
        size="lg"
      >
        {selected && (
          <Stack>
            <Group justify="space-between">
              <Text fw={700} size="lg">{selected.customer_name || "Anonim"}</Text>
              <StatusBadge status={selected.status} />
            </Group>
            <Group>
              <Badge>Masa: {selected.table_name || selected.table}</Badge>
              <Badge variant="light">{selected.party_size} kişi</Badge>
              <Badge variant="light" color="cyan">{formatDateTime(selected.starts_at)}</Badge>
            </Group>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Kapora</Text>
              <Group>
                <Text>Talep: <MoneyDisplay minor={selected.deposit_minor} /></Text>
                <Text>Ödenen: <MoneyDisplay minor={selected.deposit_paid_minor} /></Text>
              </Group>
              <DepositOutcome outcome={selected.deposit_outcome} />
            </Stack>
            {selected.notes && (
              <Text size="sm" c="dimmed">📝 {selected.notes}</Text>
            )}
            <Group mt="md">
              {["DRAFT", "AWAITING_PAYMENT", "CONFIRMED"].includes(selected.status) && (
                <Button variant="light" onClick={editHandlers.open}>Düzenle</Button>
              )}
              {selected.status === "AWAITING_PAYMENT" && (
                <Button color="green" onClick={depositHandlers.open}>Kapora Al</Button>
              )}
              {selected.status === "CONFIRMED" && (
                <Button color="blue" onClick={doSeat} loading={seat.isPending}>
                  Misafirleri Masaya Geçir
                </Button>
              )}
              {selected.status !== "CANCELLED" && selected.status !== "COMPLETED"
                && selected.status !== "SEATED" && (
                <Button color="red" variant="light" onClick={openCancel} loading={cancel.isPending}>
                  İptal Et
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Drawer>

      {selected && (
        <DepositModal
          opened={depositOpen}
          onClose={depositHandlers.close}
          reservation={selected}
          onDone={() => { depositHandlers.close(); setSelectedId(null); }}
        />
      )}

      <Modal
        opened={cancelOpen}
        onClose={cancelHandlers.close}
        title="Rezervasyonu İptal Et"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Bu rezervasyonu iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </Text>
          {selected && (
            <Card withBorder padding="sm" radius="sm">
              <Stack gap={4}>
                <Text size="sm" fw={600}>{selected.customer_name || "Anonim"}</Text>
                <Text size="xs" c="dimmed">
                  🪑 {selected.table_name || selected.table} · {selected.party_size} kişi · {formatDateTime(selected.starts_at)}
                </Text>
              </Stack>
            </Card>
          )}
          <Textarea
            label="İptal nedeni (opsiyonel)"
            placeholder="Örn. müşteri telefonla iptal istedi"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={cancelHandlers.close} disabled={cancel.isPending}>
              Vazgeç
            </Button>
            <Button color="red" onClick={confirmCancel} loading={cancel.isPending}>
              İptal Et
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/**
 * Kapora akıbeti rozeti — NO_SHOW/iptal sonrası kaporanın yandığını / iade
 * edildiğini açıkça gösterir. Aktif rezervasyonda backend null döner → gizli.
 */
function DepositOutcome({ outcome }: { outcome?: { kind: string; forfeited_minor: number; refunded_minor: number } | null }) {
  if (!outcome) return null;
  if (outcome.kind === "forfeited") {
    return (
      <Badge color="red" variant="light" size="sm">
        🔥 Kapora yandı: <MoneyDisplay minor={outcome.forfeited_minor} />
      </Badge>
    );
  }
  if (outcome.kind === "refunded") {
    return (
      <Badge color="green" variant="light" size="sm">
        ↩️ İade edildi: <MoneyDisplay minor={outcome.refunded_minor} />
      </Badge>
    );
  }
  // partial
  return (
    <Badge color="orange" variant="light" size="sm">
      🔥 Yanan: <MoneyDisplay minor={outcome.forfeited_minor} /> · ↩️ İade: <MoneyDisplay minor={outcome.refunded_minor} />
    </Badge>
  );
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function CalendarView({
  reservations, day, onDayChange, onSelect,
}: {
  reservations: any[];
  day: Date;
  onDayChange: (d: Date) => void;
  onSelect: (id: string) => void;
}) {
  const dayReservations = reservations
    .filter((r: any) => r.starts_at && sameDay(new Date(r.starts_at), day))
    .sort((a: any, b: any) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());

  function shiftDay(delta: number) {
    const d = new Date(day);
    d.setDate(d.getDate() + delta);
    onDayChange(d);
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="default" size="lg" onClick={() => shiftDay(-1)}>←</ActionIcon>
          <DatePickerInput
            value={day}
            onChange={(v) => v && onDayChange(new Date(v))}
            valueFormat="DD MMMM YYYY, dddd"
            w={240}
          />
          <ActionIcon variant="default" size="lg" onClick={() => shiftDay(1)}>→</ActionIcon>
          <Button variant="subtle" size="xs" onClick={() => onDayChange(new Date())}>Bugün</Button>
        </Group>
        <Badge size="lg" variant="light">{dayReservations.length} rezervasyon</Badge>
      </Group>

      {dayReservations.length === 0 ? (
        <EmptyState
          icon="📅"
          title="Bu güne rezervasyon yok"
          description="Başka bir gün seçin veya yeni rezervasyon oluşturun"
        />
      ) : (
        <Stack gap="xs">
          {dayReservations.map((r: any) => {
            const time = new Date(r.starts_at).toLocaleTimeString("tr-TR", {
              hour: "2-digit", minute: "2-digit",
            });
            return (
              <Card
                key={r.id}
                padding="md"
                radius="md"
                withBorder
                style={{ cursor: "pointer" }}
                onClick={() => onSelect(r.id)}
              >
                <Group justify="space-between">
                  <Group>
                    <Text fw={700} size="lg" w={64}>{time}</Text>
                    <Stack gap={2}>
                      <Text fw={500}>{r.customer_name || r.customer_phone || "Anonim"}</Text>
                      <Text size="sm" c="dimmed">
                        🪑 {r.table_name || r.table} · {r.party_size} kişi
                      </Text>
                    </Stack>
                  </Group>
                  <StatusBadge status={r.status} />
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

function DepositModal({
  opened, onClose, reservation, onDone,
}: { opened: boolean; onClose: () => void; reservation: any; onDone: () => void }) {
  const remaining = Math.max(
    0, (reservation.deposit_minor || 0) - (reservation.deposit_paid_minor || 0),
  );
  const [amount, setAmount] = useState<number>(remaining / 100);
  const deposit = useAction("reservations", "deposit");

  async function submit() {
    try {
      await deposit.mutateAsync({ id: reservation.id, amount_minor: Math.round(amount * 100) });
      notifications.show({ message: "Kapora alındı", color: "green" });
      onDone();
    } catch (e: any) {
      notifications.show({ message: apiErrorMessage(e), color: "red" });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Kapora Al" size="sm">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Talep edilen: <MoneyDisplay minor={reservation.deposit_minor} /> ·
          Ödenen: <MoneyDisplay minor={reservation.deposit_paid_minor} />
        </Text>
        <NumberInput
          label="Tahsil edilecek tutar (₺)"
          required
          value={amount}
          onChange={(v) => setAmount(Number(v) || 0)}
          min={0}
          decimalScale={2}
          fixedDecimalScale
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={submit} loading={deposit.isPending} disabled={amount <= 0} color="green">
            Kapora Al
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Rezervasyon formu — `reservation` verilmezse yeni kayıt (POST), verilirse
 * düzenleme (PATCH). Düzenlemede status/kapora değişmez; onlar drawer'daki
 * Kapora Al / Masaya Geçir / İptal aksiyonlarından yönetilir.
 */
function ReservationFormModal({
  opened, onClose, reservation,
}: { opened: boolean; onClose: () => void; reservation?: any }) {
  const { data: tablesData } = useResource<any>("tables");
  const tables: any[] = tablesData?.results || tablesData || [];
  const qc = useQueryClient();
  const isEdit = !!reservation;

  const [tableId, setTableId] = useState<string | null>(null);
  const [partySize, setPartySize] = useState<number>(2);
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [notes, setNotes] = useState("");
  // Backend (DRF) alan-bazlı validation hataları — ilgili input'a inline gösterilir.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Düzenleme modunda form, seçili rezervasyonun güncel değerleriyle dolar.
  // Modal her açıldığında (veya farklı rezervasyon seçildiğinde) senkronla.
  useEffect(() => {
    if (!opened) return;
    setFieldErrors({});
    if (reservation) {
      setTableId(reservation.table ?? null);
      setPartySize(reservation.party_size ?? 2);
      setStartsAt(reservation.starts_at ? new Date(reservation.starts_at) : null);
      setCustomerName(reservation.customer_name ?? "");
      setCustomerPhone(reservation.customer_phone ?? "");
      setNotes(reservation.notes ?? "");
    } else {
      setTableId(null); setPartySize(2); setStartsAt(null);
      setCustomerName(""); setCustomerPhone(""); setNotes("");
    }
  }, [opened, reservation]);

  const updateReservation = useUpdateReservation();

  const phoneError = validatePhoneTR(customerPhone);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        table: tableId,
        party_size: partySize,
        starts_at: startsAt?.toISOString(),
        customer_name: customerName,
        customer_phone: customerPhone,
        notes,
      };
      if (isEdit) {
        return updateReservation.mutateAsync({ id: reservation.id, patch: payload });
      }
      return (await apiClient.post("/api/v1/reservations/", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reservations"] });
      notifications.show({
        message: isEdit ? "Rezervasyon güncellendi" : "Rezervasyon oluşturuldu",
        color: "green",
      });
      onClose();
    },
    onError: (e: any) => {
      // Alan-bazlı hatalar ilgili input'un altında gösterilir; ayrıca özet bir
      // bildirim çıkar (örn. "Geçmiş bir tarih/saate rezervasyon oluşturulamaz").
      setFieldErrors(apiFieldErrors(e));
      notifications.show({ message: apiErrorMessage(e), color: "red" });
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={isEdit ? "Rezervasyonu Düzenle" : "Yeni Rezervasyon"} size="md">
      <Stack gap="md">
        <Select
          label="Masa"
          required
          data={tables.map((t: any) => ({ value: t.id, label: `${t.name} (${t.capacity} kişi)` }))}
          value={tableId}
          onChange={setTableId}
          searchable
          error={fieldErrors.table || fieldErrors.non_field_errors || null}
        />
        <Group grow align="flex-start">
          <NumberInput label="Kişi Sayısı" required value={partySize} onChange={(v) => setPartySize(Number(v) || 1)} min={1} error={fieldErrors.party_size || null} />
          <DateTimePicker label="Tarih / Saat" required value={startsAt} onChange={(v) => setStartsAt(v as any)} minDate={new Date()} error={fieldErrors.starts_at || null} />
        </Group>
        <Group grow>
          <TextInput label="Müşteri Adı" value={customerName} onChange={(e) => setCustomerName(e.currentTarget.value)} />
          <TextInput label="Telefon" type="tel" inputMode="tel" placeholder="+90 555 123 45 67" value={customerPhone} onChange={(e) => setCustomerPhone(e.currentTarget.value)} error={customerPhone ? phoneError : null} />
        </Group>
        <Textarea label="Not" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!tableId || !startsAt || !!phoneError}>
            {isEdit ? "Kaydet" : "Oluştur"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
